import { useEffect, useId, useState } from 'react';
import { normalizeDriveLabel, DRIVE_STATUSES, PROJECT_DRIVE_ROLES, driveCapacity, nextDriveRole, type DriveListRow, type DriveRow, type ProjectDrive, type ProjectDriveInput, type ProjectDriveRole } from '../../shared/drives';
import { api } from '../lib/api';
import { DriveEditor } from './DriveEditor';

export function withDriveRole(links: ProjectDriveInput[], id: string, role: ProjectDriveRole): ProjectDriveInput[] {
  return links.map((l) => ({ drive_id: l.drive_id, role: l.drive_id === id ? role : role !== 'archive' && l.role === role ? 'archive' : l.role }));
}

export function DrivePickerBody({ links, drives, disabled = false, onChange, onCreate }: {
  links: ProjectDrive[]; drives: DriveListRow[]; disabled?: boolean;
  onChange: (links: ProjectDriveInput[]) => void; onCreate: (label: string) => void;
}) {
  const manual = links.filter((drive) => !drive.auto_backup);
  const automatic = links.filter((drive) => !!drive.auto_backup);
  const id = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const options = DRIVE_STATUSES.flatMap((s) => drives.filter((d) => !d.auto_backup && d.status === s.value && !links.some((l) => l.drive_id === d.id) && d.label.toLowerCase().includes(normalizeDriveLabel(query).toLowerCase())));
  function choose(index: number) {
    if (disabled) return;
    if (index === options.length) onCreate(normalizeDriveLabel(query));
    else if (options[index]) onChange([...manual.map(({ drive_id, role }) => ({ drive_id, role })), { drive_id: options[index].id, role: nextDriveRole(manual) }]);
    setQuery(''); setOpen(false); setActive(0);
  }
  return <div className="drive-picker" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
    <div className="field-label">Archive drives</div>
    <div className="drive-links">{manual.map((d) => <div className="drive-link" key={d.drive_id}>
      <span className="nm" title={d.label}>{d.label}{d.status !== 'active' && <small>{d.status}</small>}</span>
      <select className={`role-sel ${d.role}`} aria-label={`Role for ${d.label}`} title="Choosing Primary or Backup moves the previous drive in that role to Archive" value={d.role} disabled={disabled} onChange={(e) => onChange(withDriveRole(manual, d.drive_id, e.target.value as ProjectDriveRole))}>
        {PROJECT_DRIVE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <button className="x" aria-label={`Remove ${d.label}`} disabled={disabled} onClick={() => onChange(manual.filter((l) => l.drive_id !== d.drive_id).map(({ drive_id, role }) => ({ drive_id, role })))}>×</button>
    </div>)}</div>
    {automatic.map((drive) => <div className="drive-link auto" key={drive.drive_id}><span className="nm">{drive.label} (auto)</span><span className="reg-sub" title="Linked automatically; cannot be edited">Backup · locked</span></div>)}
    {!manual.length && <p className="reg-sub">No linked drives.</p>}
    <div className="combo">
      <input className="combo-input" role="combobox" aria-label="Add archive drive" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-options`} aria-activedescendant={open ? `${id}-${active}` : undefined}
        disabled={disabled} placeholder="Search drives…" value={query} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActive((n) => Math.max(0, Math.min(options.length, n + (e.key === 'ArrowDown' ? 1 : -1)))); }
          if (e.key === 'Enter' && open) { e.preventDefault(); choose(active); }
        }} />
      {open && <div className="drive-options" role="listbox" id={`${id}-options`} aria-label="Available drives">
        {DRIVE_STATUSES.map((s) => {
          const group = options.filter((d) => d.status === s.value);
          return group.length > 0 && <div role="group" aria-label={s.label} key={s.value}><div className="dd-sec">{s.label}</div>{group.map((d) => {
            const index = options.indexOf(d);
            return <button type="button" role="option" aria-selected={active === index} id={`${id}-${index}`} key={d.id} tabIndex={-1}
              className={`combo-opt ${d.status}${index === active ? ' selected' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(index)}>
              <span className="nm">{d.label}</span><span className="sub">{d.kind} · {driveCapacity(d.capacity_gb)}</span>
            </button>;
          })}</div>;
        })}
        <button type="button" role="option" aria-selected={active === options.length} id={`${id}-${options.length}`} tabIndex={-1} className={`combo-opt create${active === options.length ? ' selected' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(options.length)}>+ New drive{normalizeDriveLabel(query) ? ` “${normalizeDriveLabel(query)}”` : ''}</button>
      </div>}
    </div>
  </div>;
}

export function DrivePicker({ projectId }: { projectId: string }) {
  const [links, setLinks] = useState<ProjectDrive[]>([]);
  const [drives, setDrives] = useState<DriveListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [newLabel, setNewLabel] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    Promise.all([api<{ drives: ProjectDrive[] }>(`/api/admin/projects/${projectId}/drives`), api<{ drives: DriveListRow[] }>('/api/admin/drives')])
      .then(([linked, all]) => { if (live) { setLinks(linked.drives); setDrives(all.drives); } })
      .catch((e: Error) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [projectId, retry]);
  async function change(next: ProjectDriveInput[]) {
    setBusy(true); setError('');
    try { setLinks((await api<{ drives: ProjectDrive[] }>(`/api/admin/projects/${projectId}/drives`, { method: 'PUT', json: next.filter((link) => !links.some((drive) => drive.drive_id === link.drive_id && drive.auto_backup)) })).drives); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function created(drive: DriveRow) {
    setNewLabel(null);
    setDrives((all) => [...all, { ...drive, project_count: 0 }]);
    await change([...links.filter((drive) => !drive.auto_backup).map(({ drive_id, role }) => ({ drive_id, role })), { drive_id: drive.id, role: nextDriveRole(links.filter((drive) => !drive.auto_backup)) }]);
  }
  return <div className="field" data-rail-control="archive-drive">
    {loading ? <p role="status">Loading archive drives…</p> : <DrivePickerBody links={links} drives={drives} disabled={busy || (!!error && !drives.length)} onChange={(next) => void change(next)} onCreate={setNewLabel} />}
    {busy && <span className="reg-sub" role="status">Saving drives…</span>}
    {error && <p className="drive-error" role="alert">{error} <button className="btn" onClick={() => setRetry((n) => n + 1)}>Retry</button></p>}
    {newLabel !== null && <DriveEditor seedLabel={newLabel} onClose={() => setNewLabel(null)} onSaved={created} />}
  </div>;
}
